// La pestaña Pinterest del selector de fuentes ("Añadir referencia"): lo que la persona guardó en su
// Pinterest, para elegirlo como inspiración (ADR-2026-10-23-pinterest-guardados-como-referencia).
//
// Vive aparte de `ModalDeFuentes` para no engordarlo: el pop-up sólo la monta como una pestaña más
// (`PestanaDelSelector`) y le presta su paginación. Tres estados:
//   - sin conectar → la tarjeta con "Conectar Pinterest";
//   - conectado → sus tableros (también los secretos) y "Todos mis guardados";
//   - adentro de uno → sus imágenes, que se marcan como cualquier otra fuente.
// Nada se guarda: los tableros y los pines se le piden al backend (que se los pide a Pinterest)
// cada vez que se abre la pestaña.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, Images, Lock } from 'lucide-react';
import { LogoDePinterest } from './LogoDePinterest';
import type { ChatAttachment } from '../../pages/v2/agentAttachments';
import type { PestanaDelSelector } from '../../components/SelectorDeImagenModal';
import {
  abrirVentanaDePinterest,
  CANCELADO_EN_ATLAS,
  conectarPinterestEnVentana,
  desconectarPinterest,
  ErrorDePinterest,
  esAppInstaladaEnIOS,
  estadoDePinterest,
  pinesDePinterest,
  tablerosDePinterest,
  type EstadoDePinterest,
  type PinDePinterest,
  type TableroDePinterest,
} from '../../lib/pinterestConexion';
import { mensajeDeErrorDePinterest } from '../../lib/pinterestConexionLogica';
import { adjuntoDePinterest, idDePinDePinterest } from './bibliotecaDeAdjuntos';

/** "Todos mis guardados": los pines de la persona sin importar el tablero. */
const TODOS = '__todos__';

/** `fallo`: el último pedido falló. Corta el scroll infinito (si no, el centinela lo vuelve a
 *  pedir en cada render y martilla el cupo de Pinterest) y muestra "Reintentar". */
type Lista<T> = { items: T[]; siguiente: string | null; cargando: boolean; fallo: boolean };
const vacia = <T,>(): Lista<T> => ({ items: [], siguiente: null, cargando: false, fallo: false });

const mensaje = (e: unknown) => (e instanceof ErrorDePinterest ? e.message : 'No pudimos traer tu Pinterest. Probá de nuevo en un rato.');

export function usePestanaDePinterest(p: {
  /** El pop-up está abierto: se pregunta si Pinterest está disponible y conectado. */
  abierto: boolean;
  setAviso: (texto: string | null) => void;
}): {
  /** El backend la habilita para esta persona (si no, la pestaña no se ofrece). */
  disponible: boolean;
  /** Se entró a la pestaña: recién ahí se piden los tableros (cada pedido gasta del cupo de Pinterest). */
  entrar: () => void;
  /** La pestaña, armada con lo marcado del pop-up (que se define después del hook). */
  armar: (estaMarcado: (id: string) => boolean, alternar: (adjunto: ChatAttachment) => void) => PestanaDelSelector;
  quedaPorMostrar: boolean;
  llegarAlFinal: () => void;
} {
  const { abierto, setAviso } = p;
  const [activa, setActiva] = useState(false);
  const [estado, setEstado] = useState<EstadoDePinterest | null>(null);
  const [conectando, setConectando] = useState(false);
  const [confirmarSalida, setConfirmarSalida] = useState(false);
  const [tableros, setTableros] = useState<Lista<TableroDePinterest> | null>(null);
  /** El tablero abierto (o TODOS); null = la grilla de tableros. */
  const [abiertoId, setAbiertoId] = useState<string | null>(null);
  const [pines, setPines] = useState<Map<string, Lista<PinDePinterest>>>(() => new Map());
  /** Pedidos en vuelo: el scroll dispara el centinela varias veces antes del próximo render. */
  const enVuelo = useRef(new Set<string>());
  /** Cada apertura del pop-up tiene su número: una respuesta de una apertura anterior que llega
   *  tarde se ignora (si no, cacheaba un tablero con sólo su segunda página — review #207). */
  const apertura = useRef(0);
  const cancelarConexion = useRef<AbortController | null>(null);
  const vivo = useRef(true);
  // Vuelve a `true` al montar: en dev, StrictMode monta, desmonta y monta de nuevo.
  useEffect(() => {
    vivo.current = true;
    return () => void (vivo.current = false);
  }, []);

  // Cada vez que se abre el pop-up: estado fresco y todo lo de Pinterest se vuelve a pedir.
  useEffect(() => {
    if (!abierto) return;
    apertura.current += 1;
    setEstado(null);
    setTableros(null);
    setAbiertoId(null);
    setPines(new Map());
    setConfirmarSalida(false);
    setActiva(false);
    let actual = true;
    void estadoDePinterest().then((e) => {
      if (actual && vivo.current) setEstado(e);
    });
    return () => {
      actual = false;
    };
  }, [abierto]);

  const conectada = estado?.conectada === true;

  const pedirTableros = useCallback(
    async (siguiente: string | null) => {
      const deEsta = apertura.current;
      const clave = `${deEsta}|tableros|${siguiente ?? ''}`;
      if (enVuelo.current.has(clave)) return;
      enVuelo.current.add(clave);
      setTableros((prev) => ({ ...(prev ?? vacia<TableroDePinterest>()), cargando: true, fallo: false }));
      try {
        const r = await tablerosDePinterest(siguiente);
        if (!vivo.current || deEsta !== apertura.current) return;
        setTableros((prev) => ({
          items: siguiente ? [...(prev?.items ?? []), ...r.tableros] : r.tableros,
          siguiente: r.siguiente,
          cargando: false,
          fallo: false,
        }));
      } catch (e) {
        if (!vivo.current || deEsta !== apertura.current) return;
        setTableros((prev) => ({ ...(prev ?? vacia<TableroDePinterest>()), cargando: false, fallo: true }));
        if (e instanceof ErrorDePinterest && e.code === 'reconectar') setEstado((s) => (s ? { ...s, conectada: false, estado: 'reconectar' } : s));
        setAviso(mensaje(e));
      } finally {
        enVuelo.current.delete(clave);
      }
    },
    [setAviso],
  );

  const pedirPines = useCallback(
    async (id: string, siguiente: string | null) => {
      const deEsta = apertura.current;
      const clave = `${deEsta}|pines|${id}|${siguiente ?? ''}`;
      if (enVuelo.current.has(clave)) return;
      enVuelo.current.add(clave);
      setPines((prev) => new Map(prev).set(id, { ...(prev.get(id) ?? vacia<PinDePinterest>()), cargando: true, fallo: false }));
      try {
        const r = await pinesDePinterest({ tableroId: id === TODOS ? null : id, siguiente });
        if (!vivo.current || deEsta !== apertura.current) return;
        setPines((prev) => {
          const antes = prev.get(id);
          return new Map(prev).set(id, {
            items: siguiente ? [...(antes?.items ?? []), ...r.pines] : r.pines,
            siguiente: r.siguiente,
            cargando: false,
            fallo: false,
          });
        });
      } catch (e) {
        if (!vivo.current || deEsta !== apertura.current) return;
        setPines((prev) => new Map(prev).set(id, { ...(prev.get(id) ?? vacia<PinDePinterest>()), cargando: false, fallo: true }));
        if (e instanceof ErrorDePinterest && e.code === 'reconectar') setEstado((s) => (s ? { ...s, conectada: false, estado: 'reconectar' } : s));
        setAviso(mensaje(e));
      } finally {
        enVuelo.current.delete(clave);
      }
    },
    [setAviso],
  );

  // Los tableros se piden al entrar a la pestaña, no antes.
  useEffect(() => {
    if (activa && conectada && tableros === null) void pedirTableros(null);
  }, [activa, conectada, tableros, pedirTableros]);

  const abrir = (id: string) => {
    setAviso(null);
    setAbiertoId(id);
    const lista = pines.get(id);
    // Un tablero que falló sin traer nada se vuelve a pedir al abrirlo de nuevo.
    if (!lista || (lista.fallo && lista.items.length === 0)) void pedirPines(id, null);
  };

  // Abre la ventana en el MISMO click (Safari bloquea `window.open` después de un await).
  const conectar = () => {
    if (conectando) return;
    setAviso(null);
    if (esAppInstaladaEnIOS()) {
      setAviso(mensajeDeErrorDePinterest('app_instalada'));
      return;
    }
    const popup = abrirVentanaDePinterest();
    const control = new AbortController();
    cancelarConexion.current = control;
    setConectando(true);
    conectarPinterestEnVentana(popup, control.signal)
      .then((r) => {
        if (!vivo.current) return;
        setEstado({ disponible: true, conectada: true, estado: 'activa', username: r.username, error: false });
        setTableros(null);
        setPines(new Map());
      })
      .catch((e) => {
        if (!vivo.current) return;
        if (e instanceof ErrorDePinterest && e.code === CANCELADO_EN_ATLAS) return;
        setAviso(mensaje(e));
      })
      .finally(() => {
        if (cancelarConexion.current === control) cancelarConexion.current = null;
        if (vivo.current) setConectando(false);
      });
  };

  const desconectar = () => {
    setConfirmarSalida(false);
    desconectarPinterest()
      .then(() => {
        if (!vivo.current) return;
        setEstado((s) => (s ? { ...s, conectada: false, estado: null, username: null } : s));
        setTableros(null);
        setAbiertoId(null);
        setPines(new Map());
      })
      .catch((e) => vivo.current && setAviso(mensaje(e)));
  };

  const listaAbierta = abiertoId ? (pines.get(abiertoId) ?? null) : null;
  const tableroAbierto = abiertoId && abiertoId !== TODOS ? (tableros?.items.find((t) => t.id === abiertoId) ?? null) : null;
  const pinesPorId = new Map((listaAbierta?.items ?? []).map((x) => [x.id, x]));
  const entrar = useCallback(() => setActiva(true), []);

  // ── Lo que se dibuja arriba de la grilla ──
  const sinConectar = (
    <div className="mx-auto flex max-w-md flex-col items-center gap-3 rounded-2xl border border-neutral-200 bg-white px-6 py-8 text-center">
      <LogoDePinterest className="h-12 w-12" aria-hidden="true" />
      <p className="text-[15px] font-semibold text-brand-black">
        {estado?.estado === 'reconectar' ? 'Volvé a conectar tu Pinterest' : 'Usá lo que guardaste en Pinterest'}
      </p>
      <p className="text-[13px] leading-relaxed text-[rgb(var(--brand-black-rgb)_/_0.6)]">
        {estado?.estado === 'reconectar'
          ? 'Pinterest pidió volver a darnos permiso para ver lo que guardaste.'
          : 'Conectá tu cuenta y elegí imágenes de tus tableros como inspiración para crear algo parecido con tu marca. Nada de tu Pinterest se guarda en Atlas.'}
      </p>
      <button
        type="button"
        onClick={conectar}
        disabled={conectando}
        className="mt-1 inline-flex items-center gap-2 rounded-full bg-brand-primary px-5 py-2 text-[13.5px] font-semibold text-white transition-colors hover:bg-brand-primary-hover disabled:opacity-60"
      >
        {conectando ? 'Conectando…' : estado?.estado === 'reconectar' ? 'Volver a conectar' : 'Conectar Pinterest'}
      </button>
      {conectando && (
        <button type="button" onClick={() => cancelarConexion.current?.abort()} className="text-[13px] font-semibold text-brand-primary hover:underline">
          Cancelar
        </button>
      )}
    </div>
  );

  // Cuando una carga falla: el aviso dice qué pasó y esto deja volver a intentar.
  const reintentar = (
    <div className="flex flex-wrap items-center justify-center gap-3 py-6 text-[13px] text-[rgb(var(--brand-black-rgb)_/_0.6)]">
      <span>No pudimos traer esto de tu Pinterest.</span>
      <button
        type="button"
        onClick={() => {
          setAviso(null);
          if (abiertoId) void pedirPines(abiertoId, listaAbierta?.items.length ? (listaAbierta.siguiente ?? null) : null);
          else void pedirTableros(tableros?.items.length ? (tableros.siguiente ?? null) : null);
        }}
        className="font-semibold text-brand-primary hover:underline"
      >
        Reintentar
      </button>
    </div>
  );

  const cuenta = (
    <div className="flex flex-wrap items-center justify-between gap-2 pb-3 text-[12.5px] text-[rgb(var(--brand-black-rgb)_/_0.55)]">
      <span>{estado?.username ? `Conectado como @${estado.username}` : 'Pinterest conectado'}</span>
      {confirmarSalida ? (
        <span className="inline-flex items-center gap-2">
          <span>¿Desconectar tu Pinterest?</span>
          <button type="button" onClick={desconectar} className="font-semibold text-[#C0392B] hover:underline">
            Desconectar
          </button>
          <button type="button" onClick={() => setConfirmarSalida(false)} className="font-semibold text-brand-primary hover:underline">
            Cancelar
          </button>
        </span>
      ) : (
        <button type="button" onClick={() => setConfirmarSalida(true)} className="font-semibold text-brand-primary hover:underline">
          Desconectar
        </button>
      )}
    </div>
  );

  const grillaDeTableros = (
    <div className="pb-2">
      {cuenta}
      <div className="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3 md:grid-cols-5">
        <button type="button" onClick={() => abrir(TODOS)} className="group flex flex-col gap-1.5 text-left">
          <span className="flex aspect-square w-full items-center justify-center rounded-xl bg-[rgb(var(--brand-primary-rgb)_/_0.095)] text-brand-primary transition-colors group-hover:bg-[rgb(var(--brand-primary-rgb)_/_0.15)]">
            <Images className="h-7 w-7" aria-hidden="true" />
          </span>
          <span className="truncate text-[13px] font-semibold text-brand-black">Todos mis guardados</span>
        </button>
        {(tableros?.items ?? []).map((t) => (
          <button key={t.id} type="button" onClick={() => abrir(t.id)} className="group flex min-w-0 flex-col gap-1.5 text-left">
            <span className="relative block aspect-square w-full overflow-hidden rounded-xl bg-neutral-100">
              {t.portada && (
                <img src={t.portada} alt="" loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-[1.03]" />
              )}
              {t.secreto && (
                <span className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-brand-black" title="Tablero secreto">
                  <Lock className="h-3.5 w-3.5" aria-label="Tablero secreto" />
                </span>
              )}
            </span>
            <span className="truncate text-[13px] font-semibold text-brand-black">{t.nombre}</span>
            {t.cantidad != null && <span className="-mt-1 text-[12px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">{t.cantidad} pines</span>}
          </button>
        ))}
      </div>
    </div>
  );

  const cabeceraDelTablero = (
    <div className="flex flex-wrap items-center gap-2 pb-4">
      <button
        type="button"
        onClick={() => setAbiertoId(null)}
        className="inline-flex items-center gap-1 rounded-full py-1.5 pl-2 pr-3 text-[13px] font-semibold text-brand-primary transition-colors hover:bg-[rgb(var(--brand-primary-rgb)_/_0.095)]"
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
        Pinterest
      </button>
      <p className="min-w-0 flex-1 truncate text-[14px] font-semibold text-brand-black">
        {abiertoId === TODOS ? 'Todos mis guardados' : (tableroAbierto?.nombre ?? 'Tablero')}
      </p>
    </div>
  );

  const armar = (estaMarcado: (id: string) => boolean, alternar: (adjunto: ChatAttachment) => void): PestanaDelSelector => ({
    id: 'pinterest',
    label: 'Pinterest',
    Icon: LogoDePinterest,
    items:
      conectada && listaAbierta
        ? listaAbierta.items.map((x) => ({
            id: x.id,
            url: x.miniatura,
            nombre: x.titulo ?? 'Imagen de Pinterest',
            seleccionado: estaMarcado(idDePinDePinterest(x.id)),
          }))
        : [],
    cargando: estado === null || (conectada && (abiertoId ? !!listaAbierta?.cargando && !listaAbierta.items.length : tableros === null || (tableros.cargando && !tableros.items.length))),
    vacio: conectada && abiertoId && !listaAbierta?.fallo ? 'Este tablero no tiene imágenes para elegir.' : '',
    aspecto: 'retrato',
    conNombres: false,
    encabezado:
      estado === null ? undefined : !conectada ? (
        sinConectar
      ) : abiertoId ? (
        <>
          {cabeceraDelTablero}
          {listaAbierta?.fallo && reintentar}
        </>
      ) : (
        <>
          {grillaDeTableros}
          {tableros?.fallo && reintentar}
        </>
      ),
    onElegir: (item) => {
      setAviso(null);
      const pin = pinesPorId.get(item.id);
      if (pin) alternar(adjuntoDePinterest(pin));
    },
  });

  const siguienteActual = !conectada ? null : abiertoId ? (listaAbierta?.siguiente ?? null) : (tableros?.siguiente ?? null);
  const cargandoActual = abiertoId ? !!listaAbierta?.cargando : !!tableros?.cargando;
  // Si la última página falló, el scroll no la vuelve a pedir: queda "Reintentar".
  const falloActual = abiertoId ? !!listaAbierta?.fallo : !!tableros?.fallo;
  return {
    disponible: estado?.disponible === true,
    entrar,
    armar,
    quedaPorMostrar: !!siguienteActual && !falloActual,
    llegarAlFinal: () => {
      if (!siguienteActual || cargandoActual || falloActual) return;
      if (abiertoId) void pedirPines(abiertoId, siguienteActual);
      else void pedirTableros(siguienteActual);
    },
  };
}
